import * as React from 'react';
import {reportBug} from '@packages/utilities/reporter';
import lottie from 'lottie-web';
import {get, set, Store} from 'idb-keyval';
import {RENDER_MODES} from '@packages/systems/core/constants/Lottie';
import {fetchLottie} from '@packages/systems/core/utils/LottieFetchUtils';

const {useRef, useEffect, useState} = React;

/**
 * This custom hook is responsible for getting the animationData in order to load it into
 * Lottie. It first checks if this JSON was previously saved in the user's browser via
 * IndexedDB, and if not fetches it and then triggers a rerender via setState.
 *
 */
export const useLottieIndexDB = (path: string | null, isUploading: boolean) => {
  const lottieIndexedDBRef = useRef(undefined);
  const [animationData, setAnimationData] = useState(null);

  useEffect(() => {
    if (path == null) {
      return;
    }

    if (lottieIndexedDBRef.current == null) {
      // @ts-expect-error - TS2322 - Type 'Store' is not assignable to type 'undefined'.
      // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
      lottieIndexedDBRef.current = new Store('lottie-db', 'lottie-store');
    }

    const LottieJsonStore = lottieIndexedDBRef.current;

    // If JSON is stored in indexedDB, get it to avoid making a network call
    get(path, LottieJsonStore).then((value) => {
      if (value) {
        // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type 'SetStateAction<null>'.
        setAnimationData(value);
      } else {
        if (!isUploading) {
          fetchLottie(path).then((data) => {
            // We have to make a brand new copy because the browser does not allow us to have
            // the same object reference in IndexedDB AND used somewhere else in the app, so we
            // make a copy — one for IndexedDB, and the other to use memory for the app.
            const animationDataCopy = JSON.parse(JSON.stringify(data));
            // @ts-expect-error - TS2345 - Argument of type 'LottieJson' is not assignable to parameter of type 'SetStateAction<null>'.
            setAnimationData(data);
            set(path, animationDataCopy, LottieJsonStore);
          });
        }
      }
    });
  }, [path, isUploading]);

  return animationData;
};

type AnimationEventName =
  | 'enterFrame'
  | 'loopComplete'
  | 'complete'
  | 'segmentStart'
  | 'destroy'
  | 'config_ready'
  | 'data_ready'
  | 'DOMLoaded'
  | 'error'
  | 'data_failed'
  | 'loaded_images';

const defaultRendererSettings = {
  progressiveLoad: true,
  preserveAspectRatio: 'xMidYMid meet',
} as const;

const defaultConfig = {
  autoplay: false,
  loop: false,
  rendererSettings: defaultRendererSettings,
  eventListeners: [],
  renderer: RENDER_MODES.SVG,
  isUploading: false,
} as const;

export const useLottie = (
  path: string,
  // @ts-expect-error - TS2322 - Type '{ readonly autoplay: false; readonly loop: false; readonly rendererSettings: { readonly progressiveLoad: true; readonly preserveAspectRatio: "xMidYMid meet"; }; readonly eventListeners: readonly []; readonly renderer: "svg"; readonly isUploading: false; }' is not assignable to type '{ autoplay?: boolean | undefined; loop?: number | boolean | undefined; rendererSettings?: { progressiveLoad?: boolean | undefined; preserveAspectRatio?: string | undefined; } | undefined; renderer?: "svg" | ... 1 more ... | undefined; eventListeners?: [...][] | undefined; isUploading?: boolean | undefined; }'.
  config: {
    autoplay?: boolean;
    loop?: number | boolean;
    rendererSettings?: {
      progressiveLoad?: boolean;
      preserveAspectRatio?: string;
    };
    renderer?: (typeof RENDER_MODES)[keyof typeof RENDER_MODES];
    eventListeners?: Array<[AnimationEventName, (arg1?: any) => void]>;
    isUploading?: boolean;
  } = defaultConfig
) => {
  const containerRef = useRef<any>(undefined);
  const instanceRef = useRef(undefined);

  const {
    autoplay = defaultConfig.autoplay,
    loop = defaultConfig.loop,
    renderer = defaultConfig.renderer,
    eventListeners = defaultConfig.eventListeners,
    isUploading = false,
    rendererSettings: {
      preserveAspectRatio = defaultRendererSettings.preserveAspectRatio,
      progressiveLoad = defaultRendererSettings.progressiveLoad,
    } = defaultRendererSettings,
  } = config;

  const animationData = useLottieIndexDB(path, isUploading);

  useEffect(
    () => {
      const container = containerRef.current;

      if (container == null || animationData == null) {
        return;
      }

      let instance = instanceRef.current;

      if (instance) {
        // @ts-expect-error - TS2339 - Property 'destroy' does not exist on type 'never'.
        instance.destroy();
      }

      // @ts-expect-error - TS2322 - Type 'AnimationItem' is not assignable to type 'undefined'.
      instance = lottie.loadAnimation({
        container,
        animationData,
        autoplay,
        loop,
        renderer,
        rendererSettings: {preserveAspectRatio, progressiveLoad},
      });

      if (eventListeners) {
        eventListeners.forEach(([eventName, listener]: [any, any]) =>
          // @ts-expect-error lib missing flow types
          instance.addEventListener(eventName, listener)
        );
      }

      instanceRef.current = instance;
    },
    // Eslint is requesting for eventListeners which are
    // objs whose values don't need to change. Ignoring it here since adding it
    // would cause this effect to potentially run on every render due to shallow reference
    // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      path,
      isUploading,
      animationData,
      autoplay,
      loop,
      renderer,
      preserveAspectRatio,
      progressiveLoad,
    ]
  );

  // Destroy the instance when the component unmounts
  useEffect(() => {
    return () => {
      if (instanceRef.current) {
        try {
          // @ts-expect-error - TS2339 - Property 'destroy' does not exist on type 'never'.
          instanceRef.current.destroy();
          // @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'undefined'.
          instanceRef.current = null;
        } catch (e: any) {
          reportBug(
            'LottieInvalidJSONFile',
            'Attempted to destroy lottie animation on unmount, but an error is thrown. Culprit could be be a bad JSON file.',
            {
              team: 'DXP',
              priority: 'warning',
              ticket: 'https://webflow.atlassian.net/browse/DESXP-3710',
              error: e,
              path,
            }
          );
        }
      }
    };
  }, []);

  return {instance: instanceRef.current, containerRef};
};

const PLAYBACK_DELAY = 200;

const useHoverPlay = (instance: any | null | undefined, hover: boolean) => {
  const timeoutRef = useRef(undefined);
  useEffect(() => {
    if (!instance) {
      return;
    }

    clearTimeout(timeoutRef.current);

    if (hover) {
      // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'undefined'.
      // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
      timeoutRef.current = setTimeout(() => {
        instance.play();
      }, PLAYBACK_DELAY);
    } else {
      if (!instance.isPaused) {
        instance.stop();
      }
    }
  }, [hover, instance]);
};

export const Lottie: React.ComponentType<{
  path: string;
  hover: boolean;
  isUploading: boolean;
  renderer?: (typeof RENDER_MODES)[keyof typeof RENDER_MODES]; // 'svg' | 'canvas';
  style?: React.CSSProperties;
  ['data-location']?: string;
  onError?: () => void;
}> = React.memo(function Lottie({
  path,
  hover,
  renderer,
  style,
  isUploading,
  'data-location': dataLocation,
  onError,
}) {
  const onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;

  // Stable ref so useLottie's effect (which intentionally excludes eventListeners
  // from its deps) doesn't re-run when onError changes identity.
  const eventListenersRef = React.useRef<
    Array<[AnimationEventName, () => void]>
  >([
    ['data_failed', () => onErrorRef.current?.()],
    ['error', () => onErrorRef.current?.()],
  ]);

  const {instance, containerRef} = useLottie(path, {
    renderer,
    isUploading,
    loop: true,
    eventListeners: eventListenersRef.current,
  });
  useHoverPlay(instance, hover);

  const additionalProps: Record<string, any> = {};

  if (dataLocation) {
    additionalProps['data-location'] = dataLocation;
  }

  return (
    <div
      data-designer-lottie
      {...additionalProps}
      style={style}
      ref={containerRef}
    />
  );
});
